import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { useWxLogin as createWxLoginAdapter } from "@/views/login/useWxLogin";
import { clearEnterpriseWeChatCallback, loginWithEnterpriseWeChatCode } from "@/views/auth/enterpriseWeChat";
import wxUtils from "@/utils/wx";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import logoUrl from "@/views/assets/images/logo.svg";
import policeInsigniaUrl from "@/views/assets/images/police-insignia.png";

const loginVisualUrl =
    "https://zhihaoscm-public.oss-cn-hangzhou.aliyuncs.com/customer-admin/lian-yun-sha-shi-admin-login-2026-01-30.webp";
const loginBackgroundUrl = "https://static.zhihaoscm.cn/admin/login/zhihao.png";

export interface EnterpriseWeChatLoginParams {
    code: string;
    source: "web";
    url: "/";
}

export interface LoginPageProps {
    /** 企业微信扫码面板返回临时 code 的适配器，便于测试替换真实 SDK。 */
    createEnterpriseWeChatLogin?: () => Promise<string>;
    /** 企业微信配置开关；未传入时沿用 Vite 环境变量判断。 */
    enterpriseWeChatConfigured?: boolean;
    /** 扫码登录成功后的首页跳转边界，默认使用浏览器首页跳转。 */
    onNavigateHome?: (path: "/") => void;
}

/** 管理后台登录页：普通浏览器默认扫码，企业微信内置浏览器交由认证门禁处理。 */
export function LoginPage({ createEnterpriseWeChatLogin, enterpriseWeChatConfigured, onNavigateHome }: LoginPageProps) {
    const [error, setError] = useState("");
    const wxLoginAdapter = useRef<ReturnType<typeof createWxLoginAdapter> | null>(null);

    // 真实环境要求同时配置企业 ID 和应用 ID；测试可显式注入开关与 SDK 适配器。
    const isEnterpriseWeChatConfigured =
        enterpriseWeChatConfigured ?? Boolean(import.meta.env.VITE_APP_ID && import.meta.env.VITE_AGENT_ID);

    /** 完成扫码并复用旧版 Vue 的 code 换取 Token 逻辑。 */
    const completeEnterpriseWeChatLogin = useCallback(
        async (loginCode: string) => {
            setError("");
            try {
                await loginWithEnterpriseWeChatCode(loginCode);
                clearEnterpriseWeChatCallback();
                (onNavigateHome ?? defaultNavigateHome)("/");
            } catch (loginError) {
                setError(getEnterpriseWeChatErrorMessage(loginError));
            } finally {
                // 扫码完成或失败后释放面板，避免离开登录页时残留 iframe。
                const activeAdapter = wxLoginAdapter.current;
                activeAdapter?.unmountLoginPanel();
                if (wxLoginAdapter.current === activeAdapter) {
                    wxLoginAdapter.current = null;
                }
            }
        },
        [onNavigateHome]
    );

    useEffect(() => {
        // AuthBootstrap 独占企微内置浏览器 OAuth，避免登录页再次触发授权而产生跳转循环。
        if (wxUtils.isWxWork()) {
            return;
        }
        if (!isEnterpriseWeChatConfigured) {
            setError("企业微信登录暂未配置");
            return undefined;
        }

        // 忽略组件卸载后的迟到回调，避免已离开登录页仍然兑换 code。
        let disposed = false;
        const adapter = createWxLoginAdapter();
        wxLoginAdapter.current = adapter;

        // Vue 旧版在普通浏览器挂载时立即创建企微扫码面板，保持相同的默认登录流程。
        const createLoginPanel = createEnterpriseWeChatLogin ?? (() => adapter.createLoginPanel());
        void createLoginPanel()
            .then(loginCode => {
                if (!disposed) {
                    void completeEnterpriseWeChatLogin(loginCode);
                }
            })
            .catch(loginError => {
                if (!disposed) {
                    setError(getEnterpriseWeChatErrorMessage(loginError));
                }
            });

        return () => {
            disposed = true;
            adapter.unmountLoginPanel();
            if (wxLoginAdapter.current === adapter) {
                wxLoginAdapter.current = null;
            }
        };
    }, [completeEnterpriseWeChatLogin, createEnterpriseWeChatLogin, isEnterpriseWeChatConfigured]);

    return (
        <main
            className="login-container"
            style={
                {
                    "--login-visual-url": `url(${loginVisualUrl})`,
                    "--login-background-url": `url(${loginBackgroundUrl})`
                } as CSSProperties
            }
        >
            <div className="login-container-left" aria-hidden="true" />
            <div className="login-container-right">
                <div className="company-info">
                    <img className="company-logo" src={logoUrl} alt="" />
                    <div className="company-copy">
                        <div className="company-name">志豪链云</div>
                        <div className="company-mission">产业新质生产力服务商</div>
                    </div>
                </div>
                <div className="login-panel">
                    <div id="ww_login" />
                    {error ? <Alert message={error} role="alert" type="error" /> : null}
                </div>
                <footer className="login-footer">
                    <div className="copyright">
                        Copyright © 2024-{new Date().getFullYear()} 志豪链云科技有限公司 All Rights Reserved
                    </div>
                    <div className="filings">
                        <span>鄂B2-20240693</span>
                        <img src={policeInsigniaUrl} alt="" />
                        <span>鄂公网安备42110002000192号</span>
                    </div>
                </footer>
            </div>
        </main>
    );
}

/** 默认首页跳转保持登录入口的边界，不额外改变路由配置。 */
function defaultNavigateHome(path: "/") {
    window.location.assign(path);
}

/** 优先展示后端返回的可读错误码，其他错误沿用 Vue 登录页提示。 */
function getEnterpriseWeChatErrorMessage(error: unknown) {
    const responseCode = (error as { response?: { data?: { code?: unknown } } })?.response?.data?.code;
    if (responseCode !== undefined && responseCode !== null && Number.isNaN(Number(responseCode))) {
        return String(responseCode);
    }
    if (error instanceof Error && error.message) {
        return error.message;
    }
    return "获取企业微信登录错误，请刷新后再试";
}

export default LoginPage;
